"use client";

/**
 * 쇼핑 필터 사이드바
 *
 * Desktop: 좌측 고정 사이드바 (w-56)
 * Mobile: Sheet 오버레이로 열림
 * URL searchParams 기반 필터 관리
 *
 * 가격 필터: filters=price:min-max 형식 (레거시 호환)
 * 백엔드 마이그레이션 후 동작 예정, 프론트 UI는 준비 완료
 */
import { useId } from "react";
import { useRouter, useSearchParams } from "next/navigation";
import { SlidersHorizontal } from "lucide-react";

import { useAuth } from "@/shared/auth/hooks";

import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";

/** 백엔드 order 파라미터 기준 + 클라이언트 가격 정렬 */
const SORT_OPTIONS = [
  { value: "best", label: "인기순" },
  { value: "recommend", label: "추천순" },
  { value: "latest", label: "최신순" },
  { value: "price_asc", label: "가격 낮은순" },
  { value: "price_desc", label: "가격 높은순" },
] as const;

/** 가격 구간 체크박스 (원 단위) */
const PRICE_RANGES = [
  { id: "0-10000", label: "1만원 미만", min: 0, max: 10000 },
  { id: "10000-30000", label: "1만 ~ 3만원", min: 10000, max: 30000 },
  { id: "30000-50000", label: "3만 ~ 5만원", min: 30000, max: 50000 },
  { id: "50000-100000", label: "5만 ~ 10만원", min: 50000, max: 100000 },
  { id: "100000-999999999", label: "10만원 이상", min: 100000, max: 999999999 },
] as const;

/** 가격 정렬은 로그인 사용자에게만 노출 (비로그인은 가격 숨김) */
const PRICE_SORT_VALUES = new Set(["price_asc", "price_desc"]);

function FilterContent() {
  const prefix = useId();
  const searchParams = useSearchParams();
  const router = useRouter();
  const { isAuthenticated } = useAuth();

  const activeSort = searchParams.get("order") ?? "best";
  const sortOptions = isAuthenticated
    ? SORT_OPTIONS
    : SORT_OPTIONS.filter((o) => !PRICE_SORT_VALUES.has(o.value));

  // filters=price:min-max → 선택된 구간 역파싱
  const filtersParam = searchParams.get("filters") ?? "";
  const activePriceRanges = new Set<string>();
  if (filtersParam.startsWith("price:")) {
    const [min, max] = filtersParam.slice(6).split("-").map(Number);
    for (const r of PRICE_RANGES) {
      if (r.min >= min && r.max <= max) activePriceRanges.add(r.id);
    }
  }

  function updateParams(updates: Record<string, string | null>) {
    const next = new URLSearchParams(searchParams.toString());
    next.delete("page");
    for (const [key, value] of Object.entries(updates)) {
      if (value === null || value === "" || value === "all") {
        next.delete(key);
      } else {
        next.set(key, value);
      }
    }
    router.push("?" + next.toString());
  }

  function handleSortChange(value: string) {
    updateParams({ order: value === "best" ? null : value });
  }

  function handlePriceRangeToggle(rangeId: string) {
    const next = new Set(activePriceRanges);
    if (next.has(rangeId)) {
      next.delete(rangeId);
    } else {
      next.add(rangeId);
    }

    if (next.size === 0) {
      updateParams({ filters: null });
      return;
    }

    // 선택된 구간의 전체 min/max 계산 → filters=price:min-max
    let min = Infinity;
    let max = 0;
    for (const id of next) {
      const range = PRICE_RANGES.find((r) => r.id === id);
      if (range) {
        if (range.min < min) min = range.min;
        if (range.max > max) max = range.max;
      }
    }
    updateParams({ filters: `price:${min}-${max}` });
  }

  function handleReset() {
    const idCategory = searchParams.get("idCategory");
    router.push(idCategory ? `?idCategory=${idCategory}` : "/shopping");
  }

  return (
    <div className="flex flex-col gap-6">
      {/* 정렬 */}
      <section>
        <h3 className="text-sm font-semibold mb-3">정렬</h3>
        <RadioGroup value={activeSort} onValueChange={handleSortChange}>
          {sortOptions.map((opt) => (
            <div key={opt.value} className="flex items-center gap-2">
              <RadioGroupItem value={opt.value} id={`${prefix}-sort-${opt.value}`} />
              <Label
                htmlFor={`${prefix}-sort-${opt.value}`}
                className="cursor-pointer font-normal text-sm"
              >
                {opt.label}
              </Label>
            </div>
          ))}
        </RadioGroup>
      </section>

      <Separator />

      {/* 가격대 — 로그인 시에만 노출 (비로그인은 가격 자체가 숨김) */}
      {isAuthenticated && (
        <>
          <section>
            <h3 className="text-sm font-semibold mb-3">가격대</h3>
            <div className="flex flex-col gap-2">
              {PRICE_RANGES.map((range) => (
                <div key={range.id} className="flex items-center gap-2">
                  <Checkbox
                    id={`${prefix}-price-${range.id}`}
                    checked={activePriceRanges.has(range.id)}
                    onCheckedChange={() => handlePriceRangeToggle(range.id)}
                  />
                  <Label
                    htmlFor={`${prefix}-price-${range.id}`}
                    className="cursor-pointer font-normal text-sm"
                  >
                    {range.label}
                  </Label>
                </div>
              ))}
            </div>
          </section>

          <Separator />
        </>
      )}

      {/* 필터 초기화 */}
      <Button variant="outline" size="sm" onClick={handleReset}>
        필터 초기화
      </Button>
    </div>
  );
}

/** Desktop: 고정 사이드바 */
export function FilterSidebar() {
  return (
    <aside className="hidden md:block w-56 shrink-0">
      <ScrollArea className="h-[calc(100vh-8rem)]">
        <FilterContent />
      </ScrollArea>
    </aside>
  );
}

/** Mobile: Sheet 트리거 버튼 */
export function FilterSheetTrigger() {
  return (
    <Sheet>
      <SheetTrigger asChild>
        <Button variant="outline" size="sm" className="md:hidden">
          <SlidersHorizontal className="h-4 w-4 mr-2" />
          필터
        </Button>
      </SheetTrigger>
      <SheetContent side="left" className="w-72 p-0">
        <SheetHeader className="p-6 pb-0">
          <SheetTitle>필터</SheetTitle>
          <SheetDescription className="sr-only">정렬, 가격대 필터</SheetDescription>
        </SheetHeader>
        <ScrollArea className="h-[calc(100vh-5rem)] px-6 pb-6">
          <FilterContent />
        </ScrollArea>
      </SheetContent>
    </Sheet>
  );
}
